
/* global dataGridRef */

import { writeFileXLSX, utils } from 'xlsx'
import saveFile from '../../libs/saveFile'
import { createTXT, createHTML } from '../../libs/createFileContent'
import { isNotEmpty } from '../../libs/utils'
import { store } from '../../store/configureStore'
import runOnceAtIntervalTime from '../../libs/runOnceAtIntervalTime'

export function nameExportedFile(date) {
  return `ProvideSupport-transcripts_${date.toLocaleString().replace(/\s/g, '_').replace(',', '')}`
}

const ID = 'transcriptId'
export function removeExtraFiltersByFilterRow(selectionFilter) {
  if (!selectionFilter || !selectionFilter[0]) {
    return {
      selectionFilter, isNeedToRemoveThisPart: true, isAll: (selectionFilter === null || selectionFilter[0] === null),
    }
  }
  for (let ii = 0; ii < selectionFilter.length; ii += 2) {
    if (typeof selectionFilter[ii][0] === 'string' && selectionFilter[ii][0] !== '!' && selectionFilter[ii][0] !== ID) {
      return {
        selectionFilter: removeExtraFiltersByFilterRow(selectionFilter.slice(ii + 2)).selectionFilter,
        isNeedToRemoveThisPart: true,
        isAll: true,
      }
    }
    if (selectionFilter[ii][0] === '!' && selectionFilter[ii][1][0] !== ID) {
      return {
        selectionFilter: removeExtraFiltersByFilterRow(selectionFilter.slice(ii + 2)).selectionFilter,
        isNeedToRemoveThisPart: true,
        isAll: false,
      }
    }
    if (Array.isArray(selectionFilter[ii][0])) {
      let { selectionFilter: newSelectionFilter, isAll } =
        removeExtraFiltersByFilterRow(selectionFilter[ii])
      if (newSelectionFilter !== null && (!newSelectionFilter || !newSelectionFilter.length)) {
        newSelectionFilter = selectionFilter.slice(ii + 2)
        let res = removeExtraFiltersByFilterRow(selectionFilter.slice(ii + 2))
        res.isAll = res.isAll !== undefined ? res.isAll : isAll
        return res
      }
      selectionFilter[ii] = newSelectionFilter
    }
  }
  return { selectionFilter }
}

export function countNumberTranscriptsByFilters(selectionFilter, totalCount) {
  let { selectionFilter: newSelectionFilter, isAll } =
    removeExtraFiltersByFilterRow([selectionFilter])
  selectionFilter = newSelectionFilter[0]
  if (!selectionFilter || !selectionFilter.length) {
    return isAll ? totalCount : 0 /* all or no one */
  }

  if (selectionFilter === null) return totalCount /* all */
  if (!selectionFilter || selectionFilter.length === 0) return 0 /* no one */
  if (!Array.isArray(selectionFilter[0])) {
    if (selectionFilter[0] === '!') return totalCount - 1 /* all but one */
    return 1 /* one */
  }
  const numberFilters = (selectionFilter.length + 1) / 2
  if (selectionFilter[0][0] === '!') return totalCount - numberFilters /* everything except those specified in the filters */
  return numberFilters /* everything specified in the filters */
}

export function saveXLSX({ transcripts, fileName, visibleColumnsDataField, texts }) {
  let content = []

  let firstLine = []
  visibleColumnsDataField.forEach(({ caption }) => {
    firstLine.push(caption)
  })
  content.push(firstLine)

  transcripts.forEach(transcript => {
    let line = []
    visibleColumnsDataField.forEach(({ dataType, dataPaths }) => {
      let cell = transcript
      for (let ii = 0; ii < dataPaths.length; ii++) {
        if (!dataPaths[ii] || !cell[dataPaths[ii]]) {
          cell = ''
          break
        } else {
          cell = cell[dataPaths[ii]]
        }
      }
      if (dataType === 'date' && cell) {
        cell = new Date(cell).toLocaleString()
      }
      line.push(cell)
    })
    content.push(line)
  })

  const worksheet = utils.aoa_to_sheet(content);
  const workbook = utils.book_new()
  utils.book_append_sheet(workbook, worksheet, 'Provide Support Transcripts')
  writeFileXLSX(workbook, fileName)
}

export function saveTXT({ transcripts, format = 'text/plain', fileName, texts, accountName }) {
  fileName = fileName || `${nameExportedFile(new Date(transcripts[0].startDate))}.txt`
  let content = ''
  transcripts.forEach((transcript, ii) => {
    const isFirst = ii === 0
    if (!isFirst) content += '\n\n\n'
    content += createTXT(transcript, texts, accountName)
  })
  saveFile(fileName, content, format)
}

export function saveHTML({ transcript, format = 'text/html', fileName, messageDisplayAreaRef }) {
  fileName = fileName || `${nameExportedFile(new Date(transcript.startDate))}.html`
  let content = createHTML(messageDisplayAreaRef)
  saveFile(fileName, content, format)
}

export function merge2Filters(filter1, filter2) {
  if (filter1 && !filter2) return filter1
  if (!filter1 && filter2) return filter2
  if (filter1 === null || filter2 === null) return null
  if (!filter1 && !filter2) return
  if (!Array.isArray(filter1[0])) filter1 = [filter1]
  if (!Array.isArray(filter2[0])) filter2 = [filter2]
  return [...filter1, 'and', ...filter2]
}
export function collectFilters({ dataGridRef, isAll }) {
  const selectionFilter = isAll ? null : dataGridRef.current.instance.state().selectionFilter
  let filter = dataGridRef.current.instance.getCombinedFilter()
  filter = merge2Filters(filter, selectionFilter)
  return { selectionFilter, filter }
}

export function getVisibleColumnsDataField(dataGridRef) {
  let visibleColumns = dataGridRef.current.instance.getVisibleColumns()
  let visibleColumnsDataField = []
  visibleColumns.forEach(visibleColumn => {
    if (visibleColumn.dataField) {
      visibleColumnsDataField.push(visibleColumn)
    }
  })
  return visibleColumnsDataField
}

export function transformLoadOptionsToQueryString(options) {
  let res = '?';
  [
    'take',
    'skip',
    'sort',
    'filter',
    'group',
    'requireTotalCount',
    'requireGroupCount',
    'totalSummary',
    'groupSummary',
  ].forEach(optionKey => {
    if (optionKey in options && isNotEmpty(options[optionKey])) {
      res += `${optionKey}=${encodeURIComponent(JSON.stringify(options[optionKey]))}&`;
    }
  });
  res = res.slice(0, -1);
  return res
}

const IS_ROWS_SELECTED_CLASS = 'is-rows-selected'
const IS_SORTER_CLASS = 'is-sorted'
const IS_FILTERED_CLASS = 'is-filtered'
const IS_GROUPED_CLASS = 'is-grouped'

export const tryAddStateClassesToDataGrid = runOnceAtIntervalTime({
  interval: 100,
  callback: ({ number, totalCount }) => {
    if (!dataGridRef?.current?.instance) {
      setTimeout(tryAddStateClassesToDataGrid, 100, { number, totalCount })
      return
    }
    const dataGridState = dataGridRef.current.instance.state()
    if (Object.keys(dataGridState).length === 0) {
      setTimeout(tryAddStateClassesToDataGrid, 100, { number, totalCount })
      return
    }
    if (totalCount === undefined) {
      totalCount = store.getState().transcriptViewer.totalCount
    }

    /* IS_ROWS_SELECTED */
    let isRowsSelected = (number !== undefined && number > 0)
      || countNumberTranscriptsByFilters(dataGridState.selectionFilter, totalCount)
    document.body.classList[isRowsSelected ? 'add' : 'remove'](IS_ROWS_SELECTED_CLASS)

    /* IS_SORTER IS_FILTERED IS_GROUPED */
    let isSorter = false
    let isFiltered = false
    let isGrouped = false
    dataGridState.columns.forEach(column => {
      if (column.sortIndex !== undefined) isSorter = true
      if (column.filterValue || column.filterValues) isFiltered = true
      if (column.groupIndex !== undefined) isGrouped = true
    });
    document.body.classList[isSorter ? 'add' : 'remove'](IS_SORTER_CLASS)
    document.body.classList[isFiltered ? 'add' : 'remove'](IS_FILTERED_CLASS)
    document.body.classList[isGrouped ? 'add' : 'remove'](IS_GROUPED_CLASS)
  },
})
